/**
 * Position Descriptions Utility
 * Provides hover descriptions for player positions
 * Descriptions can vary based on coverage type and formation (to be added later)
 */

import { CoverageType, FormationType, PlayerPositionId, PositionDescription } from '../types';

/**
 * Default descriptions for each position
 * These serve as fallbacks when no coverage-specific description exists
 */
const DEFAULT_DESCRIPTIONS: Record<PlayerPositionId, PositionDescription> = {
  wr1: {
    title: 'WR1 (X Receiver)',
    description: 'Wide receiver, typically lines up on the weak side.',
  },
  wr2: {
    title: 'WR2 (H Slot)',
    description: 'Slot receiver or TE, lines up on the weak side',
  },
  wr3: {
    title: 'WR3 (Y Slot)',
    description: 'Slot receiver or TE on the strong side.',
  },
  wr4: {
    title: 'WR4 (Z Receiver)',
    description: 'Wide receiver, typically lines up on the strong side.',
  },
  rb: {
    title: 'Running Back',
    // Was an empty string, so hovering the RB opened a tooltip with a heading and nothing under it.
    description: 'The #3 receiver. His release is what the linebacker reads first.',
  },
  cb1: {
    title: 'Cornerback 1',
    description: 'Primary corner, covers the weak side.',
  },
  cb2: {
    title: 'Cornerback 2',
    description: 'Secondary corner, covers the strong side.',
  },
  dblb1: {
    title: 'Apex 1 (Nickel or LB)',
    description: 'Hybrid defender, can play in coverage or support the run.',
  },
  dblb2: {
    title: 'Apex 2 (Nickel or LB)',
    description: 'Hybrid defender on the strong side.',
  },
  fs: {
    title: 'Free Safety',
    description: 'Deep safety, provides help over the top.',
  },
  ss: {
    title: 'Strong Safety',
    description: 'Strong safety, can support run or coverage.',
  },
  lb: {
    title: 'Linebacker',
    description: 'Middle linebacker, responsible for underneath coverage.',
  },
};

/**
 * Coverage-specific descriptions keyed by formation then coverage type.
 * This allows different descriptions per formation (e.g. trips vs doubles).
 */
type FormationCoverageDescriptions = Record<
  FormationType,
  Partial<Record<CoverageType, Partial<Record<PlayerPositionId, PositionDescription>>>>
>;

/**
 * Doubles (2x2) coverage descriptions
 */
const DOUBLES_COVERAGE_DESCRIPTIONS: Partial<Record<CoverageType, Partial<Record<PlayerPositionId, PositionDescription>>>> = {
  man: {
    cb1: {
      title: 'CB1 - Man Coverage',
      description: 'Locked onto WR1 in man-to-man coverage with safety help over the top.',
    },
    cb2: {
      title: 'CB2 - Man Coverage',
      description: 'Locked onto WR4 in man-to-man coverage with safety help over the top.',
    },
    dblb1: {
      title: 'Apex 1 - Man Coverage',
      description: 'Covers WR2 in man-to-man coverage.',
    },
    dblb2: {
      title: 'Apex 2 - Man Coverage',
      description: 'Covers WR3 in man-to-man coverage.',
    },
    lb: {
      title: 'LB - Man Coverage',
      description: 'Covers the running back in man-to-man coverage.',
    },
    fs: {
      title: 'FS - Deep Help',
      description: 'Provides deep help over the top',
    },
    ss: {
      title: 'SS - Deep Help',
      description: 'Provides deep help over the top.',
    },
  },
  cover2: {
    cb1: {
      title: 'CB1 - Cover 2 Flat',
      description: 'Responsible for the flat zone, funnels receivers inside.',
    },
    cb2: {
      title: 'CB2 - Cover 2 Flat',
      description: 'Responsible for the flat zone, funnels receivers inside.',
    },
    fs: {
      title: 'FS - Deep Half',
      description: 'Covers the deep half.',
    },
    ss: {
      title: 'SS - Deep Half',
      description: 'Covers the deep half.',
    },
    dblb1: {
      title: 'Apex 1 - Hook/Curl',
      description: 'Covers the hook/curl zone.',
    },
    dblb2: {
      title: 'Apex 2 - Hook/Curl',
      description: 'Covers the hook/curl zone.',
    },
    lb: {
      title: 'LB - Middle Zone',
      description: 'Covers the middle of the field in zone coverage.',
    },
  },
  cover4Match: {
    cb1: {
      title: 'CB1 - Match (MOD)',
      description: '- Reads WR1. If WR1 gets vertical past ~10 yards, plays man.\n- If WR1 breaks off short, drops to the deep left third.',
    },
    cb2: {
      title: 'CB2 - Match (MOD)',
      description: '- Reads WR4. If WR4 gets vertical past ~10 yards, plays man.\n- If WR4 breaks off short, drops to the deep right third.',
    },
    fs: {
      title: 'FS - Match / Rob (2-to-1)',
      description: '- Backpedals straight back reading WR2 (#2).\n- If WR2 gets vertical past ~10 yards, matches him over the top.\n- If WR2 stays short, keeps dropping deep in his quarter to rob #1 (WR1).',
    },
    ss: {
      title: 'SS - Match / Rob (2-to-1)',
      description: '- Backpedals straight back reading WR3 (#2 strong).\n- If WR3 gets vertical past ~10 yards, matches him over the top.\n- If WR3 stays short, keeps dropping deep in his quarter to rob #1 (WR4).',
    },
    dblb1: {
      title: 'Apex 1 - Wall-Read (#2)',
      description: '- Reads WR2 (#2). If WR2 runs a short out or hitch, jumps it man-to-man.\n- If WR2 gets vertical or breaks short inside, passes him off and drops to the curl-flat.',
    },
    dblb2: {
      title: 'Apex 2 - Wall-Read (#2)',
      description: '- Reads WR3 (#2 strong). If WR3 runs a short out or hitch, jumps it man-to-man.\n- If WR3 gets vertical or breaks short inside, passes him off and drops to the curl-flat.',
    },
    lb: {
      title: 'LB - Play RB (#3)',
      description: '- Keys the RB. If the RB releases on a normal route, takes him man-to-man.\n- If the RB is fast to the flat or runs an out, does not chase — holds the hook and looks for crossers.',
    },
  },
  cover4Palms: {
    cb1: {
      title: 'CB1 - Palms on #1',
      description: '- Man-to-man on WR1 by default; carry him if he goes vertical.\n- "Out" call: if WR2 breaks out in the first 5 yards, jump WR2 to the flat instead.\n- Otherwise, if WR1 breaks off short, drop to the deep quarter.',
    },
    cb2: {
      title: 'CB2 - Palms on #1',
      description: '- Man-to-man on WR4 by default; carry him if he goes vertical.\n- "Out" call: if WR3 breaks out in the first 5 yards, jump WR3 to the flat instead.\n- Otherwise, if WR4 breaks off short, drop to the deep quarter.',
    },
    fs: {
      title: 'FS - Robber-Read',
      description: '- If WR2 breaks out in the first 5 yards ("Out" call), take WR1 man-to-man over the top.\n- Carry WR2 man-to-man if he goes vertical.\n- Otherwise drop to the deep quarter, robbing WR1.',
    },
    ss: {
      title: 'SS - Robber-Read',
      description: '- If WR3 breaks out in the first 5 yards ("Out" call), take WR4 man-to-man over the top.\n- Carry WR3 man-to-man if he goes vertical.\n- Otherwise drop to the deep quarter, robbing WR4.',
    },
    dblb1: {
      title: 'Apex 1 - Wall-Read on #2',
      description: '- Man-to-man on WR2 by default.\n- On the corner\'s "Out" call (WR2 short out), pass WR2 off — sink to the flat/curl and read the QB.\n- Wall WR2 from the inside if he goes vertical, then settle in the curl-flat.',
    },
    dblb2: {
      title: 'Apex 2 - Wall-Read on #2',
      description: '- Man-to-man on WR3 by default.\n- On the corner\'s "Out" call (WR3 short out), pass WR3 off — sink to the flat/curl and read the QB.\n- Wall WR3 from the inside if he goes vertical, then settle in the curl-flat.',
    },
    lb: {
      title: 'LB - Hammer on #3',
      description: '- Man-to-man on the RB (#3) on a checkdown release.\n- If the RB is fast to the flat or stays in, look for a crosser and match the first one into the hook.',
    },
  },
  cover3: {
    cb1: {
      title: 'CB1 - Deep Left Third',
      description: '- Backpedal to deep left third zone on every route.\n- No man responsibilities — pass off any WR leaving the zone.',
    },
    cb2: {
      title: 'CB2 - Deep Right Third',
      description: '- Backpedal to deep right third zone on every route.\n- No man responsibilities — pass off any WR leaving the zone.',
    },
    fs: {
      title: 'FS - Deep Middle Third',
      description: '- Maintain deep middle third zone responsibility.\n- Read QB and break on the ball.',
    },
    ss: {
      title: 'SS - Right Flat',
      description: '- Drop to right flat / curl zone.\n- Wall off any WR entering the flat, release when they leave.',
    },
    dblb1: {
      title: 'Apex 1 - Left Flat',
      description: '- Drop to left flat / curl zone.\n- Wall off any WR entering the flat, release when they leave.',
    },
    dblb2: {
      title: 'Apex 2 - Right Hook',
      description: '- Play right hook zone.\n- Carry crossers through the zone, pass off at the boundary.',
    },
    lb: {
      title: 'LB - Left Hook',
      description: '- Play left hook zone.\n- Carry crossers through the zone, pass off at the boundary.',
    },
  },
  cover6: {
    cb1: {
      title: 'CB1 - Mod on #1',
      description: '- Man-to-man on #1 by default.\n- If #1 runs any route under 10 yards, drop to the deep quarter.',
    },
    dblb1: {
      title: 'Apex 1 - Wall on #2',
      description: '- Man-to-man on #2 by default.\n- Wall #2 from the inside when #2 goes vertical.\n- If #1 runs a short in and no one is in the flat, reroute #2 then cover #1 across the field.\n- If #1 runs a short out or hitch, immediately break to cover #1.\n- If the RB runs straight to the flats, break to cover #3 out of the flat.',
    },
    fs: {
      title: 'FS - Robber on 2-to-1',
      description: '- Take #2 vertical if #2 goes vertical (man-to-man).\n- Rob #1 if #2 is not vertical (read QB, break on ball toward #1).',
    },
    cb2: {
      title: 'CB2 - Flat/Match #1',
      description: '- First read: any short out-breaking route by #1 or #2 — play it.\n- If no short out-breaking routes: match #1 man-to-man everywhere.',
    },
    dblb2: {
      title: 'Apex 2 - Match #2',
      description: '- Man-to-man on #2 by default.\n- If #2 goes short out AND #1 goes short in: pick up #1 on the in-breaking route.',
    },
    ss: {
      title: 'SS - Deep Half',
      description: '- Play deep half zone.\n- Responsible for all deep routes on the Cover 2 side.',
    },
    lb: {
      title: 'LB - Cross/#3',
      description: '- Crosser from Cover 4 side: take the crossing route man-to-man.\n- #3 (RB) releases into a route and no crosser: cover #3.\n- No crosser and no #3 threat: defend hook area deep-to-short.',
    },
  },
  cover3Match: {
    cb1: {
      title: 'CB1 - Deep Third (MOD)',
      description: '- Reads WR1 (#1). Plays man-to-man if he gets vertical past ~10 yards.\n- If he breaks off short, drops to the deep left third.',
    },
    cb2: {
      title: 'CB2 - Deep Third (MOD)',
      description: '- Reads WR4 (#1). Plays man-to-man if he gets vertical past ~10 yards.\n- If he breaks off short, drops to the deep right third.',
    },
    dblb1: {
      title: 'Apex 1 - Apex (Match #2)',
      description: '- Mans WR2 (#2) by default.\n- Comes off to rob WR1 on a short out or curl.\n- If WR2 breaks short inside, passes him to the hook and settles the curl-hook.\n- Takes the RB if it flares fast to the left flat.',
    },
    ss: {
      title: 'SS - Apex (Match #2)',
      description: '- Mans WR3 (#2) by default.\n- Comes off to rob WR4 on a short out or curl.\n- If WR3 breaks short inside, passes him to the hook and settles the curl-hook.\n- Takes the RB if it flares fast to the right flat.',
    },
    fs: {
      title: 'FS - Deep Middle',
      description: '- Single-high safety: backpedals to the deep middle third.\n- Help over the top; no man responsibility.',
    },
    lb: {
      title: 'LB - Inside Hook',
      description: '- Plays the RB if he releases to this side and is not fast to the flat.\n- Otherwise drops to the hook and matches the first crosser; passes off to the other hook if there are multiple.',
    },
    dblb2: {
      title: 'Apex 2 - Inside Hook',
      description: '- Plays the RB if he releases to this side and is not fast to the flat.\n- Otherwise drops to the hook and matches the first crosser; passes off to the other hook if there are multiple.',
    },
  },
};

/**
 * Trips (3x1) coverage descriptions.
 * Currently mirrors doubles — will be customized per coverage later.
 */
export const TRIPS_COVERAGE_DESCRIPTIONS: Partial<Record<CoverageType, Partial<Record<PlayerPositionId, PositionDescription>>>> = {
  ...Object.fromEntries(
    Object.entries(DOUBLES_COVERAGE_DESCRIPTIONS).map(([coverage, positions]) => [
      coverage,
      positions ? { ...positions } : {},
    ])
  ),
  cover4Match: {
    cb1: {
      title: 'CB1 - Match (MOD)',
      description: '- Reads WR1. If WR1 gets vertical past ~10 yards, plays man.\n- If WR1 breaks off short, drops to the deep left third.',
    },
    cb2: {
      title: 'CB2 - Match (MOD)',
      description: '- Reads WR4. If WR4 gets vertical past ~10 yards, plays man.\n- If WR4 breaks off short, drops to the deep right third.',
    },
    dblb1: {
      title: 'Apex 1 - Wall-Read (#2)',
      description: '- Reads WR2 (#2). If WR2 runs a short out or hitch, jumps it man-to-man.\n- If WR2 gets vertical or breaks short inside, passes him off and drops to the curl-flat.',
    },
    dblb2: {
      title: 'Apex 2 - Wall-Read (#2)',
      description: '- Reads WR3 (#2 strong). If WR3 runs a short out or hitch, jumps it man-to-man.\n- If WR3 gets vertical or breaks short inside, passes him off and drops to the curl-flat.',
    },
    fs: {
      title: 'FS - Match / Rob (2-to-1)',
      description: '- Backpedals straight back reading WR2 (#2).\n- If WR2 gets vertical past ~10 yards, matches him over the top.\n- If WR2 stays short, keeps dropping deep in his quarter to rob #1 (WR1).',
    },
    ss: {
      title: 'SS - Match / Rob (2-to-1)',
      description: '- Backpedals straight back reading WR3 (#2 strong).\n- If WR3 gets vertical past ~10 yards, matches him over the top.\n- If WR3 stays short, keeps dropping deep in his quarter to rob #1 (WR4).',
    },
    lb: {
      title: 'LB - Play RB (#3)',
      description: '- Keys the RB. If the RB releases on a normal route, takes him man-to-man.\n- If the RB is fast to the flat or runs an out, does not chase — holds the hook and looks for crossers.',
    },
  },
  cover4Palms: {
    cb1: {
      title: 'CB1 - Palms on #1',
      description: '- Man-to-man on #1 by default.\n- "Out" call when #2 out in first 5 yards (cover #2 instead).\n- "Under" call when #1 runs underneath/shallow (zone drop to deep quarter).\n- "Smash" call when #1 runs hitch (zone drop to deep quarter, eyes on #2).',
    },
    dblb1: {
      title: 'Apex 1 - Wall-Read on #2',
      description: '- Man-to-man on #2 by default.\n- On "Out" call, play flat.\n- Wall #2 if vertical.\n- If #1 runs a short in and no one is in the flat, cover #1 across the field.\n- If #1 runs a hitch, immediately break to cover #1.\n- If #3 runs fast to the flat, break to cover #3.',
    },
    fs: {
      title: 'FS - Robber-Read',
      description: '- Take #1 man-to-man if #2 out in first 5 yards.\n- Take #2 vertical if #2 goes vertical.\n- Rob #1 otherwise.',
    },
    lb: {
      title: 'LB - Hammer on #3',
      description: '- "Push" call when RB out fast to flat (work to cover #2 underneath).\n- Man-to-man on #3 when RB releases but not fast to flat.',
    },
    cb2: {
      title: 'CB2 - Man on weak #1',
      description: '- Straight man-to-man on weak-side #1.\n- No exceptions.',
    },
    ss: {
      title: 'SS - Poach',
      description: '- Help on #3\'s vertical route (bracket from inside).\n- Help bracket weak-side #1 when #3 not vertical.',
    },
    dblb2: {
      title: 'Apex 2 - RB/Crosser',
      description: '- Take RB man-to-man if RB releases weak.\n- Take first crosser entering weak side if RB does not release weak.',
    },
  },
  cover6: {
    cb1: {
      title: 'CB1 - Palms on #1',
      description: '- Man-to-man on #1 default.\n- "Out" call when #2 out in first 5 yards (cover #2 instead).\n- "Under" call when #1 runs underneath/shallow (zone drop to deep quarter).\n- "Smash" call when #1 runs hitch (zone drop to deep quarter, eyes on #2).',
    },
    dblb1: {
      title: 'Apex 1 - Wall-Read on #2',
      description: '- Man-to-man on #2 default.\n- Wall #2 if vertical.\n- On "Out" call, play flat.\n- If #1 runs a short in and no one is in the flat, cover #1 across the field.\n- If #1 runs a hitch, immediately break to cover #1.\n- If #3 runs fast to the flat, break to cover #3.',
    },
    fs: {
      title: 'FS - Robber-Read',
      description: '- Take #1 man-to-man if #2 out in first 5 yards.\n- Take #2 vertical if #2 goes vertical.\n- Rob #1 otherwise.',
    },
    lb: {
      title: 'LB - Hammer on #3',
      description: '- "Push" call when RB out fast to flat (cover #2 underneath).\n- Man-to-man on #3 otherwise.',
    },
    cb2: {
      title: 'CB2 - Man on #1',
      description: '- Man-to-man on weak-side #1.\n- No exceptions.',
    },
    ss: {
      title: 'SS - Deep Half',
      description: '- Deep half.\n- No match responsibilities.',
    },
    dblb2: {
      title: 'Apex 2 - Zone/Crosser',
      description: '- Take crossing route from 3 WR side.\n- Maintain zone if no crosser.',
    },
  },
  cover3Match: {
    cb1: {
      title: 'CB1 - Deep Third (MOD)',
      description: '- Reads WR1 (#1). Plays man-to-man if he gets vertical past ~10 yards.\n- If he breaks off short, drops to the deep left third.',
    },
    cb2: {
      title: 'CB2 - Man on #1 (weak)',
      description: '- Straight man-to-man on the single weak-side WR4 (#1).\n- No exceptions.',
    },
    dblb1: {
      title: 'Apex 1 - Apex (Match #2)',
      description: '- Mans WR2 (#2) by default.\n- Comes off to rob WR1 (#1) on a short out or curl.\n- If WR2 breaks short inside, passes him to the hook.\n- Takes WR3 (#3) if it releases fast to the strong-side flat.',
    },
    ss: {
      title: 'SS - Weak Apex',
      description: '- Takes the RB man-to-man if he releases to the weak side.\n- If no RB, drops and matches crossers coming from the 3-WR side.',
    },
    fs: {
      title: 'FS - Deep Middle',
      description: '- Single-high safety: backpedals to the deep middle third.\n- Help over the top; no man responsibility.',
    },
    lb: {
      title: 'LB - Man on #3 (WR3)',
      description: '- Mans WR3 (#3, strong side) by default.\n- If WR3 runs a short out, rats to WR2 instead.\n- If WR3 runs an in-breaking route, passes him off to the other hook (Apex 2).',
    },
    dblb2: {
      title: 'Apex 2 - Middle Hook',
      description: '- Plays the hook.\n- Matches WR3 on an in-breaking route (the LB\'s pass-off).\n- If no WR3 in-breaker, covers the RB when it releases up the middle.',
    },
  },
};

const FORMATION_COVERAGE_DESCRIPTIONS: FormationCoverageDescriptions = {
  doubles: DOUBLES_COVERAGE_DESCRIPTIONS,
  trips: TRIPS_COVERAGE_DESCRIPTIONS,
};

/**
 * Get the description for a player position
 * Returns formation+coverage-specific description if available, otherwise default
 * 
 * @param positionId - The player position identifier
 * @param coverage - The current coverage type
 * @param formation - The current offensive formation (defaults to 'doubles')
 * @returns The position description
 */
export function getPositionDescription(
  positionId: PlayerPositionId,
  coverage: CoverageType,
  formation: FormationType = 'doubles'
): PositionDescription {
  // Check for formation+coverage-specific description first
  const formationDescriptions = FORMATION_COVERAGE_DESCRIPTIONS[formation];
  if (formationDescriptions) {
    const coverageDescriptions = formationDescriptions[coverage];
    if (coverageDescriptions && coverageDescriptions[positionId]) {
      return coverageDescriptions[positionId]!;
    }
  }
  
  // Fall back to default description
  return DEFAULT_DESCRIPTIONS[positionId];
}

/**
 * Get all position descriptions for a given coverage and formation
 * Useful for displaying a legend or reference
 * 
 * @param coverage - The current coverage type
 * @param formation - The current offensive formation (defaults to 'doubles')
 * @returns Record of all position descriptions
 */
export function getAllPositionDescriptions(
  coverage: CoverageType,
  formation: FormationType = 'doubles'
): Record<PlayerPositionId, PositionDescription> {
  const result = { ...DEFAULT_DESCRIPTIONS };
  
  const formationDescriptions = FORMATION_COVERAGE_DESCRIPTIONS[formation];
  if (formationDescriptions) {
    const coverageDescriptions = formationDescriptions[coverage];
    if (coverageDescriptions) {
      Object.entries(coverageDescriptions).forEach(([key, value]) => {
        if (value) {
          result[key as PlayerPositionId] = value;
        }
      });
    }
  }
  
  return result;
}
